import { Component } from '@theme/component';
import { bindPhoneFormatting, formatPhoneNumber } from '@theme/tgf-phone-format';
import { CartLinesUpdateEvent } from '@shopify/events';

export function restoredOptions(search) {
  let raw = new URLSearchParams(search).get('option-value');
  for (let attempt = 0; raw && attempt < 3; attempt++) {
    try { const value = JSON.parse(raw); return value && typeof value === 'object' && !Array.isArray(value) ? value : {}; }
    catch { try { raw = decodeURIComponent(raw); } catch { return {}; } }
  }
  return {};
}

export function conditionMatches(condition, values) {
  if (!condition) return true;
  const value = values[condition.field];
  return condition.checked ? value === true : value === condition.value;
}

class TGFOptionsPersonalizer extends Component {
  connectedCallback() { super.connectedCallback(); this.setup(); }
  disconnectedCallback() { super.disconnectedCallback(); this.controller?.abort(); }

  setup() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.config = JSON.parse(this.querySelector('[data-options-config]').textContent);
    this.copy = this.config.copy;
    this.form = this.querySelector('[data-options-form]');
    this.root = this.querySelector('tgf-options');
    this.fields = this.querySelector('[data-options-fields]');
    this.button = this.querySelector('[data-options-submit]');
    this.buttonLabel ||= this.button.textContent;
    this.uploads ||= new Map();
    this.boxes = [...this.root.querySelectorAll('[data-tgf-option-id]')];
    if (!this.initialized) {
      this.restore(this.editLine?.properties || restoredOptions(location.search), !!this.editLine);
      this.initialized = true;
    }
    for (const input of this.root.querySelectorAll('[data-phone]')) bindPhoneFormatting(input, signal);
    this.addEventListener('input', () => this.changed(), { signal });
    this.addEventListener('change', event => {
      if (event.target.matches('[data-tgf-upload]')) this.uploadFile(event.target);
      this.changed();
    }, { signal });
    this.addEventListener('click', event => {
      const info = event.target.closest('.tgf-option-popup');
      if (info) this.openInfo(info);
      const remove = event.target.closest('[data-upload-remove]');
      if (remove) this.removeUpload(remove.closest('[data-tgf-option-id]'));
    }, { signal });
    this.form.addEventListener('submit', event => { event.preventDefault(); event.stopImmediatePropagation(); this.submit(); }, { signal, capture: true });
    this.form.noValidate = true;
    this.fields.disabled = false;
    this.button.disabled = !this.config.available;
    this.updateConditions();
    this.dataset.ready = 'true';
  }

  restore(properties, editing = false) {
    const values = Array.isArray(properties) ? Object.fromEntries(properties) : properties;
    for (const box of this.boxes) {
      const value = values[box.dataset.propertyKey];
      const type = box.dataset.tgfOptionType;
      for (const input of box.querySelectorAll('[data-tgf-control]')) {
        if (input.type === 'radio') { if (value !== undefined || editing) input.checked = input.value === value; }
        else if (input.type === 'checkbox') { if (value !== undefined || editing) input.checked = value === 'Evet'; }
        else if (type === '12') {
          if (typeof value === 'string' && /^https:\/\//.test(value)) {
            const previous = this.uploads.get(box.dataset.tgfOptionId);
            if (previous?.preview) URL.revokeObjectURL(previous.preview);
            const state = { url: value };
            this.uploads.set(box.dataset.tgfOptionId, state);
            box.querySelector('[data-upload-remove]').hidden = false;
            box.querySelector('.tgf-upload-status').textContent = this.copy.upload_ready;
            box.querySelector('[data-upload-pick-label]').textContent = this.copy.replace_photo;
            this.showPhotoPreview(box, state);
          }
        } else if (value !== undefined || editing) input.value = typeof value === 'string' ? value : '';
      }
    }
  }

  configureEdit(line, buttonLabel) {
    this.editLine = { ...line, properties: Array.isArray(line.properties) ? Object.fromEntries(line.properties) : (line.properties || {}) };
    this.restore(this.editLine.properties, true);
    this.querySelector('[data-options-quantity]').value = line.quantity;
    this.buttonLabel = buttonLabel;
    this.button.textContent = buttonLabel;
    const preview = this.form.querySelector('tgf-collar-preview');
    if (preview) { preview.initialOptions = this.editLine.properties; preview.restoredModes = false; preview.schedule?.(); }
    for (const details of this.root.querySelectorAll('details')) {
      if ([...details.querySelectorAll('input[type=text]')].some(input => input.value.trim())) details.open = true;
    }
    this.changed();
  }

  changed() {
    this.updateConditions();
    for (const box of this.boxes) if (box.hasAttribute('data-invalid') && this.fieldValid(box)) this.invalid(box, false);
    if (this.completed && !this.busy && !this.uncertain) { this.completed = false; this.status(); this.querySelector('[data-options-cart]').hidden = true; }
  }

  updateConditions() {
    const selections = Object.fromEntries(this.boxes.map(box => {
      const selected = box.querySelector('input:checked');
      return [box.dataset.tgfOptionId, selected?.type === 'checkbox' ? true : selected?.dataset.tgfOptionValueId];
    }));
    for (const box of this.boxes) {
      const active = conditionMatches(box.dataset.condition ? JSON.parse(box.dataset.condition) : null, selections);
      box.dataset.active = String(active);
      if (box.dataset.condition) box.hidden = !active;
      for (const input of box.querySelectorAll('[data-tgf-control]')) input.disabled = !active;
      if (!active) this.invalid(box, false);
    }
  }

  properties() {
    this.updateConditions();
    const properties = { ...(this.editLine?.properties || {}) };
    for (const box of this.boxes) delete properties[box.dataset.propertyKey];
    for (const box of this.boxes) {
      if (box.dataset.active === 'false') continue;
      const type = box.dataset.tgfOptionType;
      const inputs = [...box.querySelectorAll('[data-tgf-control]')];
      let value = '';
      if (['3','4','5','15'].includes(type)) value = inputs.find(input => input.checked)?.value || '';
      else if (type === '13') value = inputs[0]?.checked ? 'Evet' : '';
      else if (type === '12') value = this.uploads.get(box.dataset.tgfOptionId)?.url || '';
      else if (['1','2'].includes(type)) value = String(inputs[0]?.value || '').trim();
      if (value && inputs[0]?.hasAttribute('data-phone')) value = formatPhoneNumber(value);
      if (value) properties[box.dataset.propertyKey] = value;
    }
    properties._tgf_options_profile = this.config.profile;
    return properties;
  }

  fieldValid(box) {
    if (box.dataset.active === 'false') return true;
    const input = box.querySelector('[data-tgf-control]');
    if (!input) return true;
    const upload = this.uploads.get(box.dataset.tgfOptionId);
    if (input.type === 'file') return !upload?.pending && !upload?.error && (box.dataset.required !== 'true' || !!upload?.url);
    if (box.dataset.required !== 'true') return true;
    return ['radio','checkbox'].includes(input.type) ? !!box.querySelector('input:checked') : !!input.value.trim();
  }

  invalid(box, invalid) {
    box.toggleAttribute('data-invalid', invalid);
    const message = box.querySelector('[data-option-error]');
    let errorText = this.copy.required;
    if (box.dataset.tgfOptionType === '12') {
      const upload = this.uploads.get(box.dataset.tgfOptionId);
      errorText = upload?.pending ? this.copy.uploading : upload?.error ? this.copy.upload_failed : this.copy.missing_file;
    }
    if (message) { message.hidden = !invalid; message.textContent = invalid ? errorText : ''; }
    for (const input of box.querySelectorAll('[data-tgf-control]')) {
      if (invalid) input.setAttribute('aria-invalid','true'); else input.removeAttribute('aria-invalid');
    }
  }

  validate() {
    this.updateConditions();
    let first;
    for (const box of this.boxes) {
      const invalid = !this.fieldValid(box);
      this.invalid(box, invalid);
      if (invalid) first ||= box;
    }
    if (first) {
      for (let parent=first.parentElement; parent && parent !== this; parent=parent.parentElement) if (parent.tagName==='DETAILS') parent.open=true;
      first.scrollIntoView({ block:'center', behavior:'instant' });
      first.querySelector('[data-tgf-control]')?.focus({ preventScroll:true });
      return false;
    }
    return this.form.reportValidity();
  }

  status(text = '', error = false) {
    const node = this.querySelector('[data-options-status]'); node.textContent = text; node.toggleAttribute('data-error', error);
  }

  openInfo(button) {
    const box = button.closest('[data-tgf-option-id]');
    const source = box.querySelector('[data-option-info]');
    if (!source) return;
    const dialog = document.createElement('dialog'); dialog.className='tgf-options-info';
    const header=document.createElement('header'), title=document.createElement('h2'), close=document.createElement('button');
    title.id=`TgfInfo-${crypto.randomUUID()}`; title.textContent=box.dataset.propertyKey;
    dialog.setAttribute('aria-labelledby',title.id);
    close.type='button';close.textContent='×';close.setAttribute('aria-label',this.copy.close);
    close.addEventListener('click',()=>dialog.close());header.append(title,close);
    const body=document.createElement('div');body.className='tgf-options-info-body';body.append(source.content.cloneNode(true));
    dialog.append(header,body);dialog.addEventListener('close',()=>{dialog.remove();button.focus({preventScroll:true});});
    document.body.append(dialog);dialog.showModal();close.focus({preventScroll:true});
  }

  showPhotoPreview(box, state, file) {
    const preview = box.querySelector('[data-upload-preview]');
    if (!preview) return;
    const current = () => this.isConnected && this.uploads.get(box.dataset.tgfOptionId) === state;
    let converted = false;
    preview.hidden = true;
    preview.referrerPolicy = 'no-referrer';
    preview.onload = () => { if (current()) preview.hidden = false; };
    preview.onerror = async () => {
      if (!current()) return;
      preview.hidden = true;
      if (converted || !/\.hei[cf](?:$|[?#])/i.test(file?.name || state.url)) return;
      converted = true;
      try {
        // The original stays in R2. Decode only an unsupported HEIC preview,
        // in the library's worker; ordinary images never load the decoder.
        let original = file;
        if (!original) {
          const response = await fetch(state.url, { credentials: 'omit', referrerPolicy: 'no-referrer', signal: AbortSignal.timeout(15000) });
          if (!response.ok) return;
          original = await response.blob();
        }
        if (!current()) return;
        const { heicTo } = await import('./tgf-heic-preview.js');
        const jpeg = await heicTo({ blob: original, type: 'image/jpeg', quality: 0.8 });
        if (!current()) return;
        if (state.preview) URL.revokeObjectURL(state.preview);
        state.preview = URL.createObjectURL(jpeg);
        preview.src = state.preview;
      } catch {
        // Preview failure must never discard a successfully stored original.
        if (current()) preview.hidden = true;
      }
    };
    if (file) state.preview = URL.createObjectURL(file);
    preview.src = state.preview || state.url;
  }

  removeUpload(box) {
    const previous=this.uploads.get(box.dataset.tgfOptionId);previous?.controller?.abort();
    if (previous?.preview) URL.revokeObjectURL(previous.preview);
    this.uploads.delete(box.dataset.tgfOptionId);
    box.querySelector('[data-tgf-upload]').value='';
    box.querySelector('[data-upload-preview]').hidden=true;
    box.querySelector('[data-upload-remove]').hidden=true;
    box.querySelector('.tgf-upload-status').textContent='';
    box.querySelector('[data-upload-pick-label]').textContent=this.copy.choose_photo;
    this.changed();
  }

  async uploadFile(input) {
    const box=input.closest('[data-tgf-option-id]'), file=input.files?.[0];
    if (!file) return;
    const previous=this.uploads.get(box.dataset.tgfOptionId);previous?.controller?.abort();
    if (previous?.preview) URL.revokeObjectURL(previous.preview);
    const state={pending:true,controller:new AbortController()};this.uploads.set(box.dataset.tgfOptionId,state);
    const status=box.querySelector('.tgf-upload-status');status.textContent=this.copy.uploading;
    box.querySelector('[data-upload-remove]').hidden=false;
    try {
      if (file.size>10*1024*1024 || file.size===0) throw new Error(this.copy.file_size);
      if (!/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) throw new Error(this.copy.file_type);
      if (!this.config.uploadEndpoint || !this.config.turnstileSiteKey) throw new Error(this.copy.not_ready);
      const { uploadProductPhoto } = await import('./tgf-photo-upload.js');
      const uploaded=await uploadProductPhoto(file,{endpoint:this.config.uploadEndpoint,siteKey:this.config.turnstileSiteKey,productId:String(this.config.productId),signal:state.controller.signal,container:box,label:this.copy.challenge});
      if (this.uploads.get(box.dataset.tgfOptionId)!==state) return;
      state.url=uploaded.url;state.pending=false;
      this.showPhotoPreview(box,state,file);
      status.textContent=this.copy.upload_ready;
      box.querySelector('[data-upload-pick-label]').textContent=this.copy.replace_photo;
      this.changed();
    } catch (error) {
      if (this.uploads.get(box.dataset.tgfOptionId)!==state || state.controller.signal.aborted) return;
      state.pending=false;state.error=true;
      status.textContent=error.message===this.copy.not_ready || error.message===this.copy.file_size || error.message===this.copy.file_type ? error.message : this.copy.upload_failed;
      this.invalid(box,true);
    }
  }

  async submit() {
    if (this.busy || this.uncertain || !this.config.available || !this.validate()) return;
    const quantity=Number(this.querySelector('[data-options-quantity]').value);
    if (!Number.isSafeInteger(quantity)||quantity<1) return;
    const properties=this.properties(), requestId=crypto.randomUUID();
    properties._tgf_options_request=requestId;
    this.busy=true;this.fields.disabled=true;this.button.textContent=this.editLine?this.config.cartCopy.saving:this.config.cartCopy.adding;this.status();
    let posted=false;
    try {
      const sections=[...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(node=>node.dataset.sectionId))].slice(0,5);
      const item={id:this.config.variantId,quantity,properties};
      if (this.editLine) {
        const response=await fetch(`${window.Shopify.routes.root}cart.js`,{credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(12000)});
        if(!response.ok)throw new Error();
        const latest=await response.json(),current=latest.items.find(line=>line.key===this.editLine.key);
        if(!current||Number(current.variant_id)!==Number(item.id)||current.quantity!==this.editLine.quantity)throw new Error();
      }
      posted=true;
      const response=await fetch(`${window.Shopify.routes.root}cart/${this.editLine?'change':'add'}.js`,{
        method:'POST',credentials:'same-origin',signal:AbortSignal.timeout(15000),headers:{'Content-Type':'application/json',Accept:'application/json'},
        body:JSON.stringify(this.editLine?{id:this.editLine.key,quantity,properties,sections,sections_url:location.pathname}:{items:[item],sections,sections_url:location.pathname})
      });
      const result=await response.json();
      if(!response.ok){if(response.status>=400&&response.status<500)posted=false;throw new Error();}
      const cartResponse=await fetch(`${window.Shopify.routes.root}cart.js`,{credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(12000)});
      if(!cartResponse.ok)throw new Error();
      const cart=await cartResponse.json(),added=cart.items.find(line=>line.properties?._tgf_options_request===requestId);
      if(!added||Number(added.variant_id)!==Number(item.id)||added.quantity!==quantity||Object.entries(properties).some(([key,value])=>added.properties[key]!==value))throw new Error();
      const deferred=CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({action:this.editLine?'update':'add',context:this.editLine?'cart':'product',lines:[{merchandiseId:String(item.id),quantity}],promise:deferred.promise}));
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,sections:result.sections,source:'tgf-options-personalizer',itemCount:quantity,didError:false}});
      this.completed=true;this.status(this.editLine?this.config.cartCopy.saved:this.config.cartCopy.success);this.querySelector('[data-options-cart]').hidden=false;
      if(this.editLine)this.dispatchEvent(new CustomEvent('tgf-options-saved',{bubbles:true}));
      else document.querySelector('cart-drawer-component')?.closest('theme-drawer')?.open();
    } catch {
      if(posted){this.uncertain=true;this.button.hidden=true;this.querySelector('[data-options-cart]').hidden=false;}
      this.status(posted?this.config.cartCopy.uncertain:this.config.cartCopy.error,true);
    } finally {this.busy=false;this.fields.disabled=false;this.button.textContent=this.buttonLabel;}
  }
}

if(!customElements.get('tgf-options-personalizer'))customElements.define('tgf-options-personalizer',TGFOptionsPersonalizer);
